import { Stack, Text, Title } from '@mantine/core';

type Props = {
  result: Record<string, unknown>;
};

export const ResultSections = ({ result }: Props) => {
  const title = typeof result.title === 'string' ? result.title : null;
  const sections = Array.isArray(result.sections) ? result.sections : null;
  const rest = Object.entries(result).filter(([key]) => key !== 'title' && key !== 'sections');

  return (
    <Stack gap="sm">
      {title && <Title order={3}>{title}</Title>}
      {sections?.map((section, index) => <SectionBlock key={index} value={section} />)}
      {rest.map(([key, value]) => (
        <Stack key={key} gap={4}>
          <Title order={4}>{key}</Title>
          <GenericValue value={value} />
        </Stack>
      ))}
    </Stack>
  );
};

const SectionBlock = ({ value }: { value: unknown }) => {
  if (isHeadingBody(value)) {
    return (
      <Stack gap={4}>
        <Title order={4}>{value.heading}</Title>
        <Text style={{ whiteSpace: 'pre-wrap' }}>{value.body}</Text>
      </Stack>
    );
  }
  return <GenericValue value={value} />;
};

const GenericValue = ({ value }: { value: unknown }) => {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <Text style={{ whiteSpace: 'pre-wrap' }}>{String(value)}</Text>;
  }
  if (Array.isArray(value)) {
    return (
      <Stack gap="xs">
        {value.map((item, index) => (
          <GenericValue key={index} value={item} />
        ))}
      </Stack>
    );
  }
  if (value && typeof value === 'object') {
    return (
      <Stack gap="xs">
        {Object.entries(value as Record<string, unknown>).map(([key, nested]) => (
          <Stack key={key} gap={2}>
            <Text fw={600}>{key}</Text>
            <GenericValue value={nested} />
          </Stack>
        ))}
      </Stack>
    );
  }
  return null;
};

function isHeadingBody(value: unknown): value is { heading: string; body: string } {
  return Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as { heading?: unknown }).heading === 'string' &&
      typeof (value as { body?: unknown }).body === 'string'
  );
}
